import { isImageField } from '@/domain/image/isImageField';
import { base64ToImage, imageToBase64 } from '@/utils/imageBase64Utils';
import { GRADUATION_NAME } from '@/domain/image/graduationName';
import { openDB } from 'idb';
import type { ITemplate } from '@/domain/template/ITemplate';

async function getTransaction() {
    const db = await openDB('images', 2, {
        upgrade(newDb) {
            if (newDb.objectStoreNames.contains('images')) {
                newDb.deleteObjectStore('images');
            }
            const store = newDb.createObjectStore('images', { keyPath: ['fileName', 'projectId'] });
            store.createIndex('file', ['fileName', 'projectId']);
        },
    });
    const tx = db.transaction('images', 'readwrite');
    return tx;
}

async function saveImages(template: ITemplate, projectId: string) {
    await Promise.all(
        template.configuration.fields.map(async (field) => {
            if (!isImageField(field)) {
                return;
            }
            const imgField = field;
            if (imgField.filename === GRADUATION_NAME) {
                return;
            }
            if (!imgField.url || !imgField.filename) {
                return;
            }
            const imgBlob = await (await fetch(imgField.url)).blob();
            const file = new File([imgBlob], imgField.filename);
            await saveImageToLocalStorage(file, projectId);
        }),
    );
}

async function saveImageToLocalStorage(file: File, projectId: string) {
    const base64 = await imageToBase64(file);
    const tx = await getTransaction();
    const store = tx.objectStore('images');
    const isFileAlreadyInStore = await store.index('file').get([file.name, projectId]);
    if (!isFileAlreadyInStore) {
        await store.add({ fileName: file.name, projectId, type: file.type, size: file.size, data: base64 });
        tx.commit();
    } else {
        if (isFileAlreadyInStore?.data !== base64) {
            isFileAlreadyInStore.data = base64;
            await store.put(isFileAlreadyInStore);
            tx.commit();
        }
    }
}

// Reports the images it could not restore instead of toasting them: this module is a
// technical helper, and initStore already surfaces the same failure for the HTTP path.
type TInitImagesResult = {
    errors: { filename: string; uuid: string }[];
    template: ITemplate;
};

async function initImages(template: ITemplate, projectId: string): Promise<TInitImagesResult> {
    const tx = await getTransaction();
    const store = tx.objectStore('images');
    const errors: TInitImagesResult['errors'] = [];

    const fields = await Promise.all(
        template.configuration.fields.map(async (field) => {
            if (!isImageField(field)) {
                return field;
            }
            const imgField = field;
            if (imgField.filename === GRADUATION_NAME || !imgField.filename) {
                return field;
            }

            // Every failure here is one image the user loses, never the project: a rejection
            // escapes to initStore's un-awaited Promise.all and leaves isStoreInitialized
            // false, which is the blank page this load path exists to avoid. The store can
            // refuse to read, and stored data can fail to decode.
            try {
                const file = await store.get([imgField.filename, projectId]);
                if (!file) {
                    errors.push({ filename: imgField.filename, uuid: imgField.uuid ?? '' });
                    return imgField;
                }

                imgField.url = URL.createObjectURL(base64ToImage(file));
            } catch (error) {
                console.error(`Local draft: cannot restore "${imgField.filename}"`, error);
                errors.push({ filename: imgField.filename, uuid: imgField.uuid ?? '' });
            }

            return imgField;
        }),
    );

    tx.commit();

    return {
        errors,
        template: {
            ...template,
            configuration: {
                ...template.configuration,
                fields,
            },
        },
    };
}

export { initImages, saveImages };
export type { TInitImagesResult };
